// src/pages/TermsPage.jsx
// Terms of Use & Sale for bibaelectronics.com. Linked from the footer and the
// sign-up checkbox. Same layout as PrivacyPolicyPage.jsx.
//
// The commercial terms here are lifted from what the site already promises in
// ReturnPolicyPage.jsx and ShippingPolicyPage.jsx (7-day defect window,
// replacement only, GST shown at checkout, made-to-order lead times, India
// shipping). If either of those pages changes, update the matching clause
// here and LAST_UPDATED. Drafted for review: have counsel check the
// cancellation, liability and jurisdiction clauses before relying on them.
import React from "react";
import "./ContactPage.css";
import Footer from "../Footer";

const LAST_UPDATED = "1 October 2026";
const EMAIL = "info@bibaelectronics.com";

const h2 = { fontSize: "1.3rem", margin: "28px 0 8px" };

const TermsPage = () => {
  return (
    <div className="cp-root">
      <div className="cp-breadcrumb-bar">
        <div className="cp-breadcrumbs">
          <a href="/">Home</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Terms of Use &amp; Sale</span>
        </div>
      </div>

      <section className="cp-rfq-section">
        <div className="cp-wrap" style={{ maxWidth: 860 }}>
          <span className="cp-badge">TERMS</span>
          <h1 style={{ margin: "16px 0 8px", fontSize: "2rem" }}>Terms of Use &amp; Sale</h1>
          <p style={{ margin: "0 0 24px", color: "#6b7280", fontSize: "0.9rem" }}>Last updated {LAST_UPDATED}</p>

          <div style={{ fontSize: "1rem", lineHeight: 1.8, color: "#374151" }}>
            <p>
              These terms apply when you browse bibaelectronics.com, create an account, request a quote or buy from
              BIBA Electronics Pvt. Ltd. ("BIBA", "we"), 357E, Pace City II, Sector 37, Gurugram, Haryana 122004,
              India. By using the site or placing an order you agree to them. If you do not agree, please do not use
              the site.
            </p>

            <h2 style={h2}>1. Who can buy</h2>
            <p>
              Our products are electronic components and modules for businesses, engineers and OEMs. You must be at
              least 18 and able to enter a binding contract. If you buy for a company, you confirm you are
              authorised to do so and that the company is bound by these terms.
            </p>

            <h2 style={h2}>2. Your account</h2>
            <p>
              Give us accurate details and keep your sign-in secure. You are responsible for activity under your
              account. Tell us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> if you think it has been misused. We may
              suspend an account that is used to break these terms or the law.
            </p>

            <h2 style={h2}>3. Products and information</h2>
            <p>
              We take care that product descriptions, images, specifications and datasheets are accurate, but they
              can contain errors and change as designs are revised. Check the current datasheet and confirm the
              part is right for your design before ordering. Products are sold for your own electronics use. You are
              responsible for testing them in your application, including safety and regulatory compliance of the
              end product.
            </p>

            <h2 style={h2}>4. Prices and payment</h2>
            <p>
              Prices are in Indian rupees. GST (currently 18% on the items shown in the cart) and shipping are shown
              as separate lines before you pay. We may correct an obvious pricing or listing error; if we do after
              you ordered, we will tell you and ask you to confirm the corrected price; if you do not, we will cancel
              that order and refund it in full.
              Online payments are processed through Zoho Pay; we do not see or store your full card details. Where
              cash on delivery is offered, you pay the courier when the parcel arrives.
            </p>

            <h2 style={h2}>5. Orders and quotes</h2>
            <p>
              Your order is an offer to buy. A contract is formed when we confirm it by email or dispatch it. We may
              decline or cancel an order, for example if an item is unavailable, a price was wrong, or we suspect
              fraud, and will refund anything you paid. A quote is valid only for the period stated on it, and its
              terms apply to the order it covers.
            </p>
            <p>
              <b>No cancellations.</b> All orders are non-cancellable once placed, whether the items are in stock or
              made to order, and whether you paid online or chose cash on delivery. Please check the items,
              quantities and address carefully before you place an order. Made-to-order items are built or sourced
              for you; their lead time is shown on the product page and counted from payment confirmation.
            </p>

            <h2 style={h2}>6. Shipping and delivery</h2>
            <p>
              Delivery times, charges, split shipments and tracking are described in our{" "}
              <a href="/about/shipping-policy">Shipping Policy</a>. The online store ships within India; for export,
              ask us for a quote. Delivery dates are estimates. Risk of loss passes to you on delivery to the
              address you gave. Please inspect the parcel on arrival and note visible damage with the courier.
            </p>

            <h2 style={h2}>7. No returns, and replacement of faulty items</h2>
            <p>
              <b>All sales are final: items are non-returnable and cannot be exchanged or refunded</b> for change of
              mind, wrong quantity or wrong choice, or because they were used, installed or damaged after delivery.
              The only exception is an item that arrives defective, not working, damaged or missing: report it
              within 7 days of delivery and we will send a replacement, including the courier cost, as set out in our{" "}
              <a href="/about/return-policy">Return Policy</a>. That policy is our whole warranty on products sold through the
              website, unless a written quote or datasheet says otherwise. This does not limit any right you have
              under law that cannot be excluded.
            </p>

            <h2 style={h2}>8. Acceptable use</h2>
            <p>
              Do not misuse the site: no attempts to break or overload it, scrape it at scale, access other
              people's accounts, upload harmful code, or use it unlawfully or to submit false orders or enquiries.
            </p>

            <h2 style={h2}>9. Our content</h2>
            <p>
              The text, images, logos, datasheets, designs and software on this site belong to BIBA or our licensors
              and are protected by law. You may view and print them for your own business use in connection with our
              products, but may not copy, republish or sell them without our written permission. Product and brand
              names of others belong to their owners.
            </p>

            <h2 style={h2}>10. Limits of liability</h2>
            <p>
              The site and its information are provided "as is". To the extent the law allows, BIBA is not liable
              for indirect or consequential loss (such as lost profit, lost production or the cost of redesign), and
              our total liability for any claim relating to an order is limited to the price you paid for the product
              concerned. Nothing here limits liability that cannot lawfully be limited, including for fraud or
              death or personal injury caused by negligence.
            </p>

            <h2 style={h2}>11. Privacy and cookies</h2>
            <p>
              How we handle your data is explained in our <a href="/about/privacy-policy">Privacy Policy</a> and{" "}
              <a href="/about/cookie-policy">Cookie Policy</a>.
            </p>

            <h2 style={h2}>12. Events outside our control</h2>
            <p>
              We are not responsible for delay or failure caused by events beyond our reasonable control, such as
              courier or supplier disruption, natural events, strikes, government action or failures of internet or
              power services.
            </p>

            <h2 style={h2}>13. Governing law and disputes</h2>
            <p>
              These terms are governed by the laws of India. Please contact us first so we can try to resolve any
              issue directly. If we cannot, the courts at Gurugram, Haryana have exclusive jurisdiction.
            </p>

            <h2 style={h2}>14. Changes and contact</h2>
            <p>
              We may update these terms; the date above shows the latest version, and orders are governed by the
              version in force when you placed them. Questions: <a href={`mailto:${EMAIL}`}>{EMAIL}</a> or +91 95610
              91506.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TermsPage;
